/**
 * FIN-8268: trava do boleto nos últimos dias de venda da oferta.
 *
 * O corte é `availableUntil` menos N dias corridos no calendário de Brasília,
 * mantendo a hora do dia. O BFF só expõe `boletoCutoffDays`; o corte é
 * calculado aqui. Contar em dias corridos de Brasília (e não em 24h) mantém a
 * hora do dia estável mesmo que o fuso mude de offset.
 */
export const BOLETO_CUTOFF_TIME_ZONE = 'America/Sao_Paulo';

const zonedFormatter = new Intl.DateTimeFormat('en-US', {
  timeZone: BOLETO_CUTOFF_TIME_ZONE,
  hourCycle: 'h23',
  year: 'numeric',
  month: 'numeric',
  day: 'numeric',
  hour: 'numeric',
  minute: 'numeric',
  second: 'numeric',
});

/** Relógio de parede de Brasília, expresso como se fosse UTC. */
function toZonedWallClockMs(utcMs: number): number {
  const parts: Record<string, number> = {};
  zonedFormatter.formatToParts(new Date(utcMs)).forEach(({ type, value }) => {
    if (type !== 'literal') parts[type] = Number(value);
  });

  return Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute,
    parts.second
  );
}

/** Offset de Brasília (ms) em um instante: relógio de parede menos UTC. */
function getZoneOffsetMs(utcMs: number): number {
  // Trunca os ms: o formatter só enxerga segundos.
  const flooredMs = Math.floor(utcMs / 1000) * 1000;
  return toZonedWallClockMs(flooredMs) - flooredMs;
}

/**
 * Instante do corte do boleto (`availableUntil` menos `days` dias corridos de
 * Brasília, mesma hora do dia). `null` sem data limite válida ou sem
 * `days` inteiro >= 1 (null/ausente = sem trava).
 */
export function computeBoletoCutoff(
  availableUntil: string | null | undefined,
  days: number | null | undefined
): Date | null {
  if (!availableUntil) return null;
  if (typeof days !== 'number' || !Number.isInteger(days) || days < 1) {
    return null;
  }

  const untilMs = Date.parse(availableUntil);
  if (Number.isNaN(untilMs)) return null;

  const subMs = untilMs % 1000;
  const untilWallMs = toZonedWallClockMs(untilMs) + subMs;
  const wallDate = new Date(untilWallMs);
  const targetWallMs = Date.UTC(
    wallDate.getUTCFullYear(),
    wallDate.getUTCMonth(),
    wallDate.getUTCDate() - days,
    wallDate.getUTCHours(),
    wallDate.getUTCMinutes(),
    wallDate.getUTCSeconds(),
    wallDate.getUTCMilliseconds()
  );

  // Dois passes resolvem o offset do instante de destino (que pode ser
  // diferente do offset de `availableUntil`).
  const firstGuessMs = targetWallMs - getZoneOffsetMs(untilMs);
  return new Date(targetWallMs - getZoneOffsetMs(firstGuessMs));
}

interface BoletoLockParams {
  availableUntil: string | null | undefined;
  boletoCutoffDays: number | null | undefined;
  /** Instante de referência em ms; padrão = agora. */
  nowMs?: number;
}

/**
 * Boleto travado: passou do corte e a oferta ainda está no ar (antes de
 * `availableUntil`; depois disso a oferta encerrada é outra frente).
 */
export function isBoletoLocked({
  availableUntil,
  boletoCutoffDays,
  nowMs = Date.now(),
}: BoletoLockParams): boolean {
  const cutoff = computeBoletoCutoff(availableUntil, boletoCutoffDays);
  if (!cutoff) return false;

  const untilMs = Date.parse(availableUntil as string);

  return nowMs > cutoff.getTime() && nowMs <= untilMs;
}
